/**
 * Contract Termination Form Component
 * US-CONT-006: Terminate Contract
 * Form for terminating a contract with reason and notes
 */

import { useState, useCallback, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TerminationReasonSelect } from './TerminationReasonSelect';
import type {
  TerminationReason,
  TerminationFormState,
  TerminationFormErrors,
  ValidationResult
} from '../types/contract-termination.types';
import {
  MIN_NOTES_LENGTH,
  MAX_NOTES_LENGTH,
  DEFAULT_TERMINATION_FORM_STATE
} from '../types/contract-termination.types';

interface ContractTerminationFormProps {
  onSubmit: (data: TerminationFormState) => void;
  onChange?: (data: TerminationFormState, isValid: boolean) => void;
  isLoading?: boolean;
  initialData?: Partial<TerminationFormState>;
  className?: string;
}

export function ContractTerminationForm({
  onSubmit,
  onChange,
  isLoading = false,
  initialData,
  className
}: ContractTerminationFormProps) {
  const [formData, setFormData] = useState<TerminationFormState>({
    ...DEFAULT_TERMINATION_FORM_STATE,
    ...initialData
  });

  const [errors, setErrors] = useState<TerminationFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  /**
   * Validate form data
   */
  const validateForm = useCallback((data: TerminationFormState): ValidationResult => {
    const newErrors: TerminationFormErrors = {};

    // Validate reason
    if (!data.reason) {
      newErrors.reason = 'Please select a termination reason';
    }

    // Validate notes
    if (!data.notes.trim()) {
      newErrors.notes = 'Please provide additional details';
    } else if (data.notes.trim().length < MIN_NOTES_LENGTH) {
      newErrors.notes = `Details must be at least ${MIN_NOTES_LENGTH} characters`;
    } else if (data.notes.trim().length > MAX_NOTES_LENGTH) {
      newErrors.notes = `Details must not exceed ${MAX_NOTES_LENGTH} characters`;
    }

    // Validate "Other" reason requires more detailed explanation
    if (data.reason === 'OTHER' && data.notes.trim().length < 50) {
      newErrors.notes = 'Please provide a detailed explanation when selecting "Other" (minimum 50 characters)';
    }

    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors
    };
  }, []);

  /**
   * Handle reason change
   */
  function handleReasonChange(value: TerminationReason) {
    const newData = { ...formData, reason: value };
    setFormData(newData);
    setTouched({ ...touched, reason: true });

    const validation = validateForm(newData);
    setErrors(validation.errors);

    if (onChange) {
      onChange(newData, validation.isValid);
    }
  }

  /**
   * Handle notes change
   */
  function handleNotesChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const newData = { ...formData, notes: event.target.value };
    setFormData(newData);

    const validation = validateForm(newData);
    setErrors(validation.errors);

    if (onChange) {
      onChange(newData, validation.isValid);
    }
  }

  /**
   * Handle notes blur
   */
  function handleNotesBlur() {
    setTouched({ ...touched, notes: true });
  }

  /**
   * Handle form submit
   */
  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    // Mark all fields as touched
    setTouched({
      reason: true,
      notes: true
    });

    const validation = validateForm(formData);
    setErrors(validation.errors);

    if (validation.isValid) {
      onSubmit(formData);
    }
  }

  // Notify parent of initial state
  useEffect(() => {
    if (onChange) {
      const validation = validateForm(formData);
      onChange(formData, validation.isValid);
    }
  }, []); // Run only once on mount

  const notesCharCount = formData.notes.length;
  const notesCharRemaining = MAX_NOTES_LENGTH - notesCharCount;
  const showNotesError = touched.notes && errors.notes;

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-6', className)} noValidate>
      {/* Termination Reason */}
      <TerminationReasonSelect
        value={formData.reason}
        onChange={handleReasonChange}
        error={touched.reason ? errors.reason : undefined}
        disabled={isLoading}
        required
      />

      {/* Additional Details */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="termination-notes" className="text-sm font-medium">
            Additional Details
            <span className="text-destructive ml-1" aria-label="required">*</span>
          </Label>
          <span
            className={cn(
              'text-xs',
              notesCharRemaining < 100 ? 'text-orange-600' : 'text-muted-foreground',
              notesCharRemaining <= 0 && 'text-destructive'
            )}
            aria-live="polite"
          >
            {notesCharCount}/{MAX_NOTES_LENGTH}
          </span>
        </div>

        <Textarea
          id="termination-notes"
          placeholder={`Provide detailed information about the termination (minimum ${MIN_NOTES_LENGTH} characters)...`}
          value={formData.notes}
          onChange={handleNotesChange}
          onBlur={handleNotesBlur}
          disabled={isLoading}
          required
          maxLength={MAX_NOTES_LENGTH}
          rows={6}
          className={cn(
            'resize-none',
            showNotesError && 'border-destructive focus:ring-destructive'
          )}
          aria-invalid={!!showNotesError}
          aria-describedby={showNotesError ? 'termination-notes-error' : 'termination-notes-hint'}
        />

        {showNotesError ? (
          <div
            id="termination-notes-error"
            className="flex items-center gap-1.5 text-sm text-destructive"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <span>{errors.notes}</span>
          </div>
        ) : (
          <p id="termination-notes-hint" className="text-xs text-muted-foreground">
            {formData.reason === 'OTHER'
              ? 'Please provide a comprehensive explanation since you selected "Other"'
              : 'Include relevant details about the termination for record keeping'}
          </p>
        )}
      </div>

      {/* Form validation summary for screen readers */}
      <div className="sr-only" role="status" aria-live="polite">
        {Object.keys(errors).length > 0 && (
          <span>
            Form has {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * Hook to use form externally
 */
export function useTerminationForm(initialData?: Partial<TerminationFormState>) {
  const [formData, setFormData] = useState<TerminationFormState>({
    ...DEFAULT_TERMINATION_FORM_STATE,
    ...initialData
  });
  const [isValid, setIsValid] = useState(false);

  const handleFormChange = useCallback((data: TerminationFormState, valid: boolean) => {
    setFormData(data);
    setIsValid(valid);
  }, []);

  return {
    formData,
    isValid,
    handleFormChange,
    reset: () => {
      setFormData({ ...DEFAULT_TERMINATION_FORM_STATE, ...initialData });
      setIsValid(false);
    }
  };
}
